import { get } from 'lodash';
import CommonApi, { BindToClass } from 'common/utils/Api';
import logger from 'itrvl-logger';
import Camps from './facade/Camps';
import Questions from './facade/Questions';

const log = logger(__filename);

export default class Api extends CommonApi {
  constructor(server) {
    super(server);
    // name spaced the endpoints for clarity
    this.camps = {};
    BindToClass(Camps, this, 'camps');
    this.questions = {};
    BindToClass(Questions, this, 'questions');
  }

  async getData(url) {
    //  log.debug(`getData ${url}`);
    try {
      const request = await this.get(url);
      if (request.status === 200) {
        return request.data;
      }
    } catch (err) {
      log.warn('error getting request', url, err);
      throw err;
    }
  }

  loadClientConfig() {
    return this.get('/_api/config?type=client');
  }

  duplicateItinerary(itineraryId, body) {
    return this.postWithCredentials(`/api/Itineraries/${itineraryId}/duplicate`, body);
  }
  updateBookingPartyName(payload) {
    return this.post('/api/WindowAPIs/UpdateBookingPartyName', { payload });
  }
  enquireTrip2(lead) {
    return this.post('/api/Info/enquireTrip2', { lead });
  }
  enquireTrip(savedItineraryId, agentId, firstName, lastName, email, country, phone, travelMonth, message, url, noEmail) {
    return this.post('/api/Info/enquireTrip', {
      savedItineraryId,
      agentId,
      firstName,
      lastName,
      email,
      country,
      phone,
      travelMonth,
      message,
      url,
      noEmail,
    });
  }
  setClientInitialPassword(token, password) {
    return this.post(`/api/Clients/set-initial-password`, {
      token,
      password,
    });
  }
  verifyToken(verificationToken) {
    return this.post(`/api/Clients/verify-token/`, {
      token: verificationToken,
    });
  }
  clientForgotPassword(email) {
    return this.post('/api/Clients/reset', { email: email });
  }
  clientRegister(firstName, lastName, email, password) {
    return this.post('/api/Clients', {
      firstName,
      lastName,
      fullName: `${firstName} ${lastName}`,
      email: email,
      password: password,
    });
  }
  updateClient(clientId, payload) {
    return this.patch(`/api/Clients/${clientId}`, { ...payload });
  }
  clientInfo(clientId) {
    const filter = { where: { id: clientId } };
    return this.get(`/api/Clients?filter=${JSON.stringify(filter)}`);
  }
  clientSetPassword(password) {
    return this.post('/api/Clients/set-password', { password });
  }
  clientResetPassword(token, password) {
    return this.post(`/api/Clients/reset-password?access_token=${token}`, { newPassword: password });
  }
  clientResendConfirmation(email) {
    return this.post('/api/Clients/resendConfirmation', { email: email });
  }
  clientLogin(email, password) {
    return this.post('/api/Clients/login', {
      email: email,
      password: password,
    });
  }
  clientLogout() {
    return this.post('/api/Clients/logout');
  }
  userInfo() {
    if (this.axios.defaults.headers.common['Authorization']) {
      return this.get('/api/Info/whoAmI');
    }
    throw new Error('No token');
  }
  userInfoByAgency(agencyId) {
    return this.get(`/api/Info/whoAmI?agencyId=${encodeURIComponent(agencyId)}`);
  }
  logMessage(message) {
    return this.post('/api/Info/logMessage', { message });
  }
  getItinerary(id, queryParams) {
    return this.get(`/api/Itineraries/${id}${queryParams ? queryParams : ''}`);
  }
  getItineraries(queryParams) {
    return this.get(`/api/Itineraries${queryParams ? queryParams : ''}`);
  }
  getItineraryMedia() {
    return this.get(`/api/Itineraries/media`);
  }
  getCamps(where = {}) {
    return this.get(`/api/Camps?filter=${JSON.stringify(where)}`);
  }
  getCamp(supplierCode) {
    const filter = { include: { relation: 'region', scope: { include: 'country' } } };
    return this.get(`/api/Camps/${supplierCode}?filter=${JSON.stringify(filter)}`);
  }
  //TODO - add getCamp(Info)Edits
  getCountry(countryCode) {
    const filter = { include: { relation: 'regions', scope: { include: 'camps' } } };
    return this.get(`/api/Countries/${countryCode}?filter=${JSON.stringify(filter)}`);
  }
  getCountryInfo(countryCode, locale = '') {
    return this.get(`/api/Countries/${countryCode}/country-info${locale ? `?locale=${locale}` : ''}`);
  }
  getRegion(regionCode) {
    const filter = { include: ['camps', 'country'] };
    return this.get(`/api/Regions/${regionCode}?filter=${JSON.stringify(filter)}`);
  }
  getRegionInfo(regionCode, locale = '') {
    return this.get(`/api/Regions/${regionCode}/region-info${locale ? `?locale=${locale}` : ''}`);
  }
  getFeatures() {
    return this.get(`/api/Features`);
  }
  getCollections() {
    return this.getData(`api/Collections`);
  }
  getCategories() {
    return this.getData(`api/Categories`);
  }
  getMediaByCamp(supplierCode, collection) {
    //    log.debug(`getMediaByCamp(${supplierCode}${collection}`);
    const filter = {
      where: { accommodation: supplierCode },
    };
    return this.get(`/api/Collections/${collection}/media?filter=${JSON.stringify(filter)}`);
  }
  getMediaByCamps(camps, collection) {
    //    log.debug(`getMediaByCamp(`, camps, `}${collection}`);
    const filter = {
      where: { accommodation: { inq: camps } },
    };
    return this.get(`/api/Collections/${collection}/media?filter=${JSON.stringify(filter)}`);
  }
  getMediaByRegions(regions, collection) {
    //    log.debug(`getMediaByRegions(`, regions, `}${collection}`);
    const filter = {
      where: { region: { inq: regions } },
    };
    return this.get(`/api/Collections/${collection}/media?filter=${JSON.stringify(filter)}`);
  }
  getMediaByCountry(countryCode, collection, limit = 1) {
    // log.debug(`getMediaByCountry(${countryCode}${collection}`);
    const filter = {
      where: { country: countryCode },
      limit,
      order: 'id desc',
    };
    return this.get(`/api/Collections/${collection}/media?filter=${JSON.stringify(filter)}`);
  }
  getMediaByRegion(regionCode, collection, limit = 1) {
    //    log.debug(`getMediaByRegion(${regionCode}${collection}`);
    const filter = {
      where: { region: regionCode },
      limit,
      order: 'id desc',
    };
    return this.get(`/api/Collections/${collection}/media?filter=${JSON.stringify(filter)}`);
  }
  getMediaByRegionCategory(regionCode, category, limit = 1) {
    //    log.debug(`getMediaByRegionCategory(${regionCode}${category}`);
    const filter = {
      where: { region: regionCode },
      limit,
      order: 'id desc',
    };
    return this.get(`/api/Categories/${category}/media?filter=${JSON.stringify(filter)}`);
  }
  getMediaByCountryCategory(countryCode, category, limit = 1) {
    //    log.debug(`getMediaByRegionCategory(${countryCode}${category}`);
    const filter = {
      where: { country: countryCode },
      limit,
      order: 'id desc',
    };
    return this.get(`/api/Categories/${category}/media?filter=${JSON.stringify(filter)}`);
  }
  async getSiteCustomizations(agencyId) {
    try {
      const filter = { include: ['lightlogos', 'darklogos', 'featuredvideos', 'termsandconditions'] };
      const response = await this.get(`/api/Agencies/${agencyId}?filter=${JSON.stringify(filter)}`);
      const data = {
        ...response.data,
      };
      // coerce arrays to single entries
      const types = [
        ['featuredvideos', 'featuredVideo'],
        ['darklogos', 'logoDark'],
        ['lightlogos', 'logoLight'],
      ];
      for (const type of types) {
        const [collection, target] = type;
        if (get(data, collection, []).length) {
          data[target] = data[collection][0];
        } else {
          data[target] = '';
        }
        delete data[collection];
      }
      return data;
    } catch (err) {
      log.warn('getCustomization', err);
    }
  }
  getQuestions(locale) {
    return this.get('/api/Questions?filter[order]=order%20ASC&filter[locale]=' + locale);
  }
  requestHold(id) {
    return this.post(`/api/Itineraries/${id}/requestHold`);
  }
  assignItineraryPaymentsToClient(itineraryId, clientId) {
    return this.post(`/api/Itineraries/${itineraryId}/assignItineraryPaymentsToClient/${clientId}`);
  }
  generateEnquirePayments(itineraryId) {
    return this.post(`/api/Itineraries/${itineraryId}/generateEnquirePayments`);
  }
  confirmBooking(itineraryId, processor, amount, serviceFee, paymentId, paymentMethod, data, localData, paymentIds) {
    const payload = {
      processor,
      paymentMethod,
      data,
      localData,
    };

    if (paymentIds?.length) {
      // Multiple payments - amounts calculated from payment instances
      payload.paymentIds = paymentIds;
      payload.paymentId = '';
    } else {
      // Single payment - use provided amounts
      payload.paymentId = paymentId;
      payload.amount = amount;
      payload.serviceFee = serviceFee;
    }

    return this.post(`/api/Itineraries/${itineraryId}/confirm`, payload);
  }
  sendWireTransferDetails(itineraryId, data) {
    return this.post(`/api/Itineraries/${itineraryId}/sendWireTransferDetails`, data);
  }
  handleStripe(itineraryId, data) {
    return this.post(`/api/Itineraries/${itineraryId}/stripe`, data);
  }
  handleStripeAch(itineraryId, data) {
    return this.post(`/api/Itineraries/${itineraryId}/stripe/ach`, data);
  }
  confirmAchPayment(itineraryId, data) {
    return this.post(`/api/Itineraries/${itineraryId}/stripe/ach/confirm`, data);
  }
  async flywireSession(itineraryId, data) {
    return (await this.post(`/api/Itineraries/${itineraryId}/flywire/session`, data))?.data;
  }
  async confirmFlywirePayment(itineraryId, data) {
    return this.post(`/api/Itineraries/${itineraryId}/flywire/confirm`, data);
  }
  async adyenAchMandate(itineraryId) {
    return (await this.post(`/api/Itineraries/${itineraryId}/adyen/ach/mandate`, {}))?.data;
  }
  async adyenPayment(itineraryId, data) {
    return (await this.post(`/api/Itineraries/${itineraryId}/adyen/payment`, data))?.data;
  }
  async adyenPaymentDetails(itineraryId, data) {
    return (await this.post(`/api/Itineraries/${itineraryId}/adyen/payment/details`, data))?.data;
  }
  async adyenCancelAuthentication(itineraryId, data) {
    return (await this.post(`/api/Itineraries/${itineraryId}/adyen/payment/cancel`, data))?.data;
  }
  handleUplift(itineraryId, data) {
    return this.post(`/api/Itineraries/${itineraryId}/uplift`, data);
  }
  getItineraryCampInfo(itineraryId, locale = '') {
    return this.get(`/api/Itineraries/${itineraryId}/camp-info${locale ? `?locale=${locale}` : ''}`);
  }
  getRenderDataItinerary(itineraryId, locale = '') {
    return this.get(`/api/PresentationEdits/renderDataItinerary?itineraryId=${itineraryId}${locale ? `&locale=${locale}` : ''}`);
  }
  getRenderDataClient(clientId, locale = '') {
    return this.get(`/api/PresentationEdits/renderDataClient?clientId=${clientId}${locale ? `&locale=${locale}` : ''}`);
  }
  getRenderDataSavedItinerary(savedItineraryId, agencyId, agentId, locale = '') {
    return this.get(
      `/api/PresentationEdits/renderDataSavedItinerary?savedItineraryId=${savedItineraryId}&agencyId=${agencyId}&agentId=${agentId}${
        locale ? `&locale=${locale}` : ''
      }`,
    );
  }
  getItineraryActivityInfo(itineraryId, locale = '') {
    return this.get(`/api/Itineraries/${itineraryId}/activity-info${locale ? `?locale=${locale}` : ''}`);
  }
  getPosterForItineraries() {
    return this.get('/api/Itineraries/getPosterForItineraries');
  }
  getPayment(paymentId) {
    return this.get(`/api/Payments/${paymentId}`);
  }
  uploadTermsSnapshot(id, data) {
    return this.axios({
      method: 'POST',
      url: `/api/Itineraries/${id}/terms`,
      data,
    });
  }
  getEntity(entityType, query) {
    return this.axios({
      method: 'GET',
      url: `/api/${entityType}${query ? `?filter=${encodeURI(JSON.stringify(query))}` : ''}`,
    });
  }
}
